import { z } from 'zod'
import { MEDIA_AUDIO_MAX_SECONDS, MEDIA_AUDIO_MIME_TYPES, MEDIA_MAX_SIZE } from './media'

const text = z.string().trim().max(10000)
const short = z.string().trim().max(300)
const url = z
  .url()
  .refine((v) => v.startsWith('https://'))
  .or(z.literal(''))
export const weddingWebSchema = z.object({
  published: z.boolean(),
  title: short,
  date: z.iso.date().or(z.literal('')),
  theme: z.enum(['classic', 'garden', 'modern']),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  coverUrl: url,
  welcome: text,
  story: text,
  venue: short,
  address: short,
  mapUrl: url,
  transport: text,
  accommodation: text,
  dressCode: text,
  faq: text,
  gifts: text,
  giftUrl: url,
  rsvpEnabled: z.boolean(),
  rsvpDeadline: z.iso.date().or(z.literal('')),
  dayEnabled: z.boolean(),
  activation: z.enum(['day', 'previous-day', 'manual']),
  manualActive: z.boolean(),
  menu: text,
  drinks: text,
  schedule: z
    .array(
      z.object({
        id: z.uuid(),
        time: z
          .string()
          .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
          .or(z.literal('')),
        title: short.min(1),
      }),
    )
    .max(100),
  areaMapUrl: url,
  contacts: text,
  wifiName: short,
  wifiPassword: short,
  breakfast: text,
  announcements: text,
  newspaperUrl: url,
  quiz: text,
  challenges: text,
  photosEnabled: z.boolean(),
  wishesEnabled: z.boolean(),
  audioEnabled: z.boolean(),
})
export type WeddingWeb = z.infer<typeof weddingWebSchema>
export const emptyWeddingWeb = (): WeddingWeb => ({
  published: false,
  title: '',
  date: '',
  theme: 'classic',
  accent: '#a83d59',
  coverUrl: '',
  welcome: '',
  story: '',
  venue: '',
  address: '',
  mapUrl: '',
  transport: '',
  accommodation: '',
  dressCode: '',
  faq: '',
  gifts: '',
  giftUrl: '',
  rsvpEnabled: true,
  rsvpDeadline: '',
  dayEnabled: false,
  activation: 'previous-day',
  manualActive: false,
  menu: '',
  drinks: '',
  schedule: [],
  areaMapUrl: '',
  contacts: '',
  wifiName: '',
  wifiPassword: '',
  breakfast: '',
  announcements: '',
  newspaperUrl: '',
  quiz: '',
  challenges: '',
  photosEnabled: true,
  wishesEnabled: true,
  audioEnabled: true,
})
export const guestContributionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('wish'),
    name: short.min(1),
    message: z.string().trim().min(1).max(2000),
  }),
  z.object({
    kind: z.literal('rsvp'),
    name: short.min(1),
    email: z.email(),
    attendance: z.boolean(),
    count: z.number().int().min(1).max(10),
    diets: short,
    notes: z.string().trim().max(2000),
  }),
])
export type GuestContribution = z.infer<typeof guestContributionSchema>
export const guestUploadSchema = z
  .object({
    kind: z.enum(['photo', 'audio']),
    name: short.min(1),
    caption: short,
    // Guest photos accept a narrower list than catalog images: no AVIF.
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', ...MEDIA_AUDIO_MIME_TYPES]),
    size: z.number().int().min(1).max(Math.max(MEDIA_MAX_SIZE.image, MEDIA_MAX_SIZE.audio)),
    duration: z.number().min(0).max(MEDIA_AUDIO_MAX_SECONDS).optional(),
  })
  .refine((v) =>
    v.kind === 'photo'
      ? v.mimeType.startsWith('image/')
      : v.mimeType.startsWith('audio/') && v.duration !== undefined && v.duration > 0,
  )
export type GuestUpload = z.infer<typeof guestUploadSchema>
export const moderationSchema = z.object({ visible: z.boolean() })
